import { ref } from 'vue';
import axios from 'axios';
import { criarCompromissoAvulso } from '~/api/generated/clients/fluxo-compromisso-avulso/criarCompromissoAvulso';
import type { CriarCompromissoAvulsoRequest } from '~/api/generated/types/CriarCompromissoAvulsoRequest';

const API_URL = import.meta.env.VITE_API_URL;

interface ItemResult {
  codigo: string;
  nome?: string;
  tipo: string;
  sucesso: boolean;
  erro?: string;
}

interface JobResult {
  totalProcessados: number;
  totalSucesso: number;
  totalFalha: number;
  resultados: ItemResult[];
}

interface JobResponse {
  status: number; // 1 = Processando, 2 = Sucesso, 3 = Falha
  resultadoJsonDocument?: JobResult;
}

export function useCriarCompromissoAvulso() {
  const status = ref<'idle' | 'loading' | 'success' | 'partial' | 'error'>('idle');
  const jobId = ref<string | null>(null);
  const result = ref<JobResult | null>(null);
  const errorMessage = ref<string | null>(null);
  let pollInterval: ReturnType<typeof setInterval> | null = null;

  const stopPolling = () => {
    if (pollInterval) {
      clearInterval(pollInterval);
      pollInterval = null;
    }
  };

  const pollJob = async (id: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      pollInterval = setInterval(async () => {
        try {
          const response = await axios.get<JobResponse>(`${API_URL}/get-job`, {
            params: { jobId: id },
            getAccessToken: true,
            timeout: 30000,
          } as any);

          const job = response.data;

          // Status: 1 = Processando, 2 = Sucesso, 3 = Falha
          if (job.status === 1) {
            // Ainda processando, continuar polling
            return;
          }

          stopPolling();

          if (job.status === 2) {
            // Job completou com sucesso
            result.value = job.resultadoJsonDocument || null;

            // Verificar se houve falhas parciais
            if (result.value && result.value.totalFalha > 0 && result.value.totalSucesso > 0) {
              status.value = 'partial';
            } else {
              status.value = 'success';
            }
            resolve();
          } else if (job.status === 3) {
            // Job falhou
            result.value = job.resultadoJsonDocument || null;
            status.value = 'error';
            errorMessage.value = result.value?.resultados
              ?.filter(r => !r.sucesso)
              ?.map(r => r.erro)
              ?.join(', ') || 'Erro ao processar compromissos';
            resolve();
          }
        } catch (error: any) {
          stopPolling();
          status.value = 'error';
          errorMessage.value = error?.message || 'Erro ao verificar status do job';
          reject(error);
        }
      }, 2000); // Poll a cada 2 segundos
    });
  };

  const submit = async (request: CriarCompromissoAvulsoRequest): Promise<void> => {
    status.value = 'loading';
    jobId.value = null;
    result.value = null;
    errorMessage.value = null;

    try {
      // Enviar o job usando cliente gerado pelo Kubb
      const response = await criarCompromissoAvulso(request);

      if (!response.jobId) {
        throw new Error('JobId não retornado pelo servidor');
      }

      jobId.value = response.jobId;

      // Iniciar polling para conclusao do job
      await pollJob(jobId.value);
    } catch (error: any) {
      status.value = 'error';
      errorMessage.value =
        error.response?.status === 400
          ? error.response?.data?.errorMessage ?? 'Tivemos um problema inesperado'
          : 'Tivemos um problema inesperado';
    }
  };

  const reset = () => {
    stopPolling();
    status.value = 'idle';
    jobId.value = null;
    result.value = null;
    errorMessage.value = null;
  };

  const setLoading = () => {
    status.value = 'loading';
  };

  return {
    status,
    jobId,
    result,
    errorMessage,
    submit,
    reset,
    stopPolling,
    setLoading,
  };
}
